import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useDealContext } from "../hooks/useDealContext";
import EmptyState from "../components/EmptyState";
import LoadingSpinner from "../components/LoadingSpinner";
import { SkeletonTable } from "../components/SkeletonLoader";
import LeaseTypeSelector from "../components/LeaseTypeSelector";
import { Users, Download, Printer, Upload, Building2, Banknote, AlertTriangle, Pencil, Check, Trash2, Plus, ShieldCheck, ShieldAlert, Info, FileText } from "lucide-react";
import RentStepEditor from "../components/RentStepEditor";
import {
  fetchDocuments,
  fetchDeal,
  updateTenantReimbursement,
  updateTenant,
  addTenant,
  deleteTenant,
  fetchTenantDetail,
  saveTenantDetail,
  resetTenantDetail,
  downloadAllAbstracts,
  type Deal,
  type DocumentMeta,
  type Tenant,
  type CamStructure,
  type SpaceStatus,
} from "../api/client";
import {
  fmtCurrency,
  fmtPSF,
  fmtSF,
  fmtDate,
  fmtPercent,
  fmtPercentRaw,
} from "../utils/format";
import { downloadCSV } from "../utils/csv";
import { useToast } from "../contexts/ToastContext";
import { friendlyError } from "../utils/errors";
import { DetailViewTrigger } from "../components/detail-views";
import type { TimeSeriesEntry } from "../components/detail-views/types";
import RentRollDetailPanel from "./RentRollDetailPanel";
import LeaseAbstractModal from "../components/LeaseAbstractModal";

interface EscalationStep {
  type: string;
  percent?: number;
  description?: string;
  year_start?: number;
  year_end?: number;
  year_range?: string;
  annual_amount?: number;
  psf?: number;
}

/** Tenant-like shape extracted from parse_result */
interface ParsedTenant {
  tenant_id: string;
  tenant_name: string;
  suite_id: string | null;
  leased_sf: number;
  lease_type: string;
  lease_start_date: string;
  lease_expiration_date: string;
  base_rent_annual_psf: number;
  rent_escalation_type: string;
  rent_escalation_value: number;
  cam_structure: { cam_type: string; tax_reimbursement?: boolean; insurance_reimbursement?: boolean };
  rent_increase_schedule: EscalationStep[];
  renewal_info: string | Record<string, unknown> | null;
  vacancy_exempt?: boolean;
}

interface VacantSpace {
  unit: string;
  tenant_name: string;
  gla_sf: number;
  asking_rent_psf: number | null;
  status: string;
  begin_date: string | null;
  end_date: string | null;
}

interface MiscIncomeItem {
  source: string;
  location: string;
  annual_amount: number;
  begin_date: string | null;
  end_date: string | null;
}

interface ValidationFlag {
  severity: "error" | "warning";
  check: string;
  tenant_id: string | null;
  tenant_name: string | null;
  field: string;
  value: unknown;
  expected_range: string;
  message: string;
}

interface ValidationData {
  flags: ValidationFlag[];
  summary: {
    errors: number;
    warnings: number;
    tenants_flagged: number;
    checks_passed: number;
    checks_total: number;
  };
}

export default function RentRollPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { deal, setDeal } = useDealContext();
  const prop = deal.property_data;
  const toast = useToast();

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editedCells, setEditedCells] = useState<Record<string, Record<string, string>>>({});
  const [savingRow, setSavingRow] = useState<number | null>(null);
  const [changedRows, setChangedRows] = useState<Set<number>>(new Set());
  // Recalculate banner is handled globally by DealLayout via _needs_recalculation flag
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ tenant_name: "", suite_id: "", square_footage: "", base_rent_annual: "", lease_type: "pending" });

  // Rent step editor state
  const [rentStepTenantIdx, setRentStepTenantIdx] = useState<number | null>(null);

  // Lease abstract modal state
  const [abstractTenant, setAbstractTenant] = useState<{tenant: any; idx: number} | null>(null);
  const [downloadingAllAbstracts, setDownloadingAllAbstracts] = useState(false);

  // Detail view state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTenantIdx, setDetailTenantIdx] = useState(0);
  const [detailTab, setDetailTab] = useState(0);
  const [detailData, setDetailData] = useState<Record<string, unknown> | null>(null);
  const [detailEntries, setDetailEntries] = useState<TimeSeriesEntry[]>([]);
  const [detailDirty, setDetailDirty] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // If deal has property_data with tenants, use that directly
  const hasPropertyData = prop && prop.tenants && prop.tenants.length > 0;

  // Otherwise, pull from parsed documents
  const [parsedTenants, setParsedTenants] = useState<ParsedTenant[]>([]);
  const [parsedGLA, setParsedGLA] = useState<number>(0);
  const [vacantSpaces, setVacantSpaces] = useState<VacantSpace[]>([]);
  const [miscIncome, setMiscIncome] = useState<MiscIncomeItem[]>([]);
  const [typeOverrides, setTypeOverrides] = useState<Record<string, { leaseType: string; camStructure: Record<string, unknown> }>>({});
  const [validation, setValidation] = useState<ValidationData | null>(null);
  const [loading, setLoading] = useState(!hasPropertyData);

  // Always load vacant spaces and misc income from parsed documents,
  // even when hasPropertyData is true (after projection). The property_data
  // has tenant data but not vacant unit data, so we need the parse result.
  const loadVacantSpaces = useCallback(async () => {
    if (!dealId) return;
    try {
      const docs = await fetchDocuments(dealId);
      const rentRoll = docs.find(
        (d: DocumentMeta) => d.file_type === "rent_roll" && d.parse_status === "complete" && d.parse_result
      );
      if (!rentRoll || !rentRoll.parse_result) return;

      const pr = rentRoll.parse_result as Record<string, unknown>;
      const allExtras = (pr.vacant_units as Array<Record<string, unknown>>) ?? [];
      const vacants: VacantSpace[] = [];
      const misc: MiscIncomeItem[] = [];

      for (const v of allExtras) {
        const name = ((v.tenant_name as string) || "").toLowerCase();
        const sf = (v.gla_sf as number) || (v.leased_sf as number) || 0;
        const fin = (v.financials as Record<string, Record<string, number>>) || {};
        const isVacant =
          v.is_vacant === true ||
          (v.space_status as string) === "vacant" ||
          (v.space_status as string) === "available" ||
          !name ||
          name.toLowerCase().includes("vacant") ||
          name.toLowerCase().includes("available");

        if (isVacant) {
          vacants.push({
            unit: (v.unit as string) || (v.suite_id as string) || "—",
            tenant_name: (v.tenant_name as string) || "Available",
            gla_sf: sf,
            asking_rent_psf: fin.rent?.psf || null,
            status: (v.lease_status as string) || "Vacant",
            begin_date: (v.begin_date as string) || null,
            end_date: (v.end_date as string) || null,
          });
        } else {
          misc.push({
            source: (v.tenant_name as string) || "Other Income",
            location: (v.unit as string) || (v.suite_id as string) || "—",
            annual_amount: fin.rent?.ann || 0,
            begin_date: (v.begin_date as string) || null,
            end_date: (v.end_date as string) || null,
          });
        }
      }

      if (vacants.length > 0) setVacantSpaces(vacants);
      if (misc.length > 0) setMiscIncome(misc);

      // Also pull misc income from operating statement if available
      const opex = docs.find(
        (d: DocumentMeta) => d.file_type === "operating_statement" && d.parse_status === "complete" && d.parse_result
      );
      if (opex?.parse_result) {
        const opexPr = opex.parse_result as Record<string, unknown>;
        const years = (opexPr.years as Array<Record<string, unknown>>) ?? [];
        if (years.length > 0) {
          const lastYear = years[years.length - 1];
          const otherInc = (lastYear.other_income as number) || 0;
          if (otherInc > 0 && misc.length === 0) {
            setMiscIncome([{
              source: "Other Income (from OS)",
              location: "—",
              annual_amount: otherInc,
              begin_date: null,
              end_date: null,
            }]);
          }
        }
      }
    } catch {
      // Non-critical — vacant spaces are supplementary data
    }
  }, [dealId]);

  useEffect(() => { loadVacantSpaces(); }, [loadVacantSpaces]);

  const loadFromDocuments = useCallback(async () => {
    if (!dealId || hasPropertyData) return;
    try {
      const docs = await fetchDocuments(dealId);
      const rentRoll = docs.find(
        (d: DocumentMeta) => d.file_type === "rent_roll" && d.parse_status === "complete" && d.parse_result
      );
      if (!rentRoll || !rentRoll.parse_result) return;

      const pr = rentRoll.parse_result as Record<string, unknown>;
      const rawTenants = (pr.tenants as Array<Record<string, unknown>>) ?? [];
      const mapped: ParsedTenant[] = rawTenants
        .map((t) => {
          const wrapper = t as Record<string, unknown>;
          const td = (wrapper.tenant as Record<string, unknown>) ?? wrapper;
          if (!td || !td.tenant_name) return null;
          return {
            tenant_id: (td.tenant_id as string) || String(Math.random()),
            tenant_name: td.tenant_name as string,
            suite_id: (td.suite_id as string) || null,
            leased_sf: (td.leased_sf as number) || 0,
            lease_type: (td.lease_type as string) || "",
            lease_start_date: (td.lease_start_date as string) || "",
            lease_expiration_date: (td.lease_expiration_date as string) || "",
            base_rent_annual_psf: (td.base_rent_annual_psf as number) || 0,
            rent_escalation_type: (td.rent_escalation_type as string) || "flat",
            rent_escalation_value: (td.rent_escalation_value as number) || 0,
            cam_structure: (td.cam_structure as ParsedTenant["cam_structure"]) || { cam_type: "gross" },
            rent_increase_schedule: (wrapper.rent_increase_schedule as EscalationStep[]) || [],
            renewal_info: wrapper.renewal_info ?? null,
          };
        })
        .filter((t): t is ParsedTenant => t !== null);

      setParsedTenants(mapped);
      setParsedGLA(mapped.reduce((s, t) => s + t.leased_sf, 0));

      // Extract validation data
      const valData = pr._validation as ValidationData | undefined;
      if (valData) setValidation(valData);

      // Vacant spaces and misc income are loaded by the separate
      // loadVacantSpaces effect (runs for both hasPropertyData paths).
    } finally {
      setLoading(false);
    }
  }, [dealId, hasPropertyData]);

  useEffect(() => { loadFromDocuments(); }, [loadFromDocuments]);

  if (loading) return <SkeletonTable rows={8} cols={7} />;

  // Use property_data tenants if available, otherwise parsed doc tenants
  const tenants: ParsedTenant[] = hasPropertyData
    ? (prop!.tenants as unknown as ParsedTenant[])
    : parsedTenants;
  const vacantSF = vacantSpaces.reduce((sum, v) => sum + (v.gla_sf || 0), 0);
  const tla = hasPropertyData ? prop!.total_leasable_area : parsedGLA + vacantSF;

  if (tenants.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No rent roll data"
        description="Upload and parse a rent roll document to see tenant detail."
        action={
          <Link
            to={`/deals/${dealId}/documents`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all duration-200 shadow-md"
          >
            <Upload className="w-4 h-4" />
            Upload Rent Roll
          </Link>
        }
      />
    );
  }

  const sorted = [...tenants].sort((a, b) => {
    const aNum = parseSuiteNum(a.suite_id);
    const bNum = parseSuiteNum(b.suite_id);
    if (aNum !== bNum) return aNum - bNum;
    return (a.suite_id || "").localeCompare(b.suite_id || "");
  });
  const totalOccupied = sorted.reduce((s, t) => s + t.leased_sf, 0);
  const totalAnnualRent = sorted.reduce((s, t) => s + (t.base_rent_annual_psf ?? 0) * t.leased_sf, 0);
  const avgRentPSF = totalOccupied > 0 ? totalAnnualRent / totalOccupied : 0;

  // Compute escalated rent PSF as of analysis start date
  const analysisStartStr = (prop?.analysis_start_date || "") as string;
  const analysisYear = analysisStartStr ? new Date(analysisStartStr).getFullYear() : new Date().getFullYear();
  const getEscalatedPSF = (t: ParsedTenant): number => {
    const startYear = t.lease_start_date ? new Date(t.lease_start_date).getFullYear() : analysisYear;
    const yearsElapsed = Math.max(0, analysisYear - startYear);
    if (yearsElapsed === 0 || !t.rent_escalation_value) return t.base_rent_annual_psf;
    if (t.rent_escalation_type === "fixed_percent") {
      return t.base_rent_annual_psf * Math.pow(1 + t.rent_escalation_value, yearsElapsed);
    }
    if (t.rent_escalation_type === "fixed_amount") {
      return t.base_rent_annual_psf + t.rent_escalation_value * yearsElapsed;
    }
    return t.base_rent_annual_psf;
  };

  // Build validation flag lookup by tenant_id and field
  const tenantFlags: Record<string, ValidationFlag[]> = {};
  if (validation) {
    for (const f of validation.flags) {
      if (f.tenant_id) {
        if (!tenantFlags[f.tenant_id]) tenantFlags[f.tenant_id] = [];
        tenantFlags[f.tenant_id].push(f);
      }
    }
  }

  const getFlagClass = (tenantId: string, field: string): string => {
    const flags = tenantFlags[tenantId];
    if (!flags) return "";
    const flag = flags.find(f => f.field === field);
    if (!flag) return "";
    return flag.severity === "error"
      ? "border-l-[3px] border-l-red-500 bg-red-50/40"
      : "border-l-[3px] border-l-honey-500 bg-honey-50/40";
  };

  const getFlagTooltip = (tenantId: string, field: string): string | undefined => {
    const flags = tenantFlags[tenantId];
    if (!flags) return undefined;
    const flag = flags.find(f => f.field === field);
    return flag?.message;
  };

  // Detect if all lease types are effectively unknown
  const allGross = sorted.every((t) => {
    const lt = (t.lease_type || "").toLowerCase();
    if (lt !== "gross") return false;
    const cam = t.cam_structure;
    if (!cam) return true;
    return !cam.tax_reimbursement && !cam.insurance_reimbursement;
  });

  function effectiveLeaseType(t: ParsedTenant): string {
    const override = typeOverrides[t.tenant_id];
    if (override) return override.leaseType;
    const lt = t.lease_type || "";
    if (allGross && lt.toLowerCase() === "gross") return "pending";
    return lt;
  }

  async function handleLeaseTypeChange(tenantId: string, newLeaseType: string, camStructure: Record<string, unknown>) {
    setTypeOverrides(prev => ({
      ...prev,
      [tenantId]: { leaseType: newLeaseType, camStructure },
    }));
    try {
      await updateTenantReimbursement(dealId!, tenantId, {
        lease_type: newLeaseType,
        cam_structure: camStructure as Partial<CamStructure>,
      });
    } catch (err) {
      console.error("Failed to save lease type:", err);
    }
  }

  // --- Edit mode handlers ---

  function handleCellChange(rowIdx: number, field: string, value: string) {
    setEditedCells(prev => ({
      ...prev,
      [rowIdx]: { ...(prev[rowIdx] || {}), [field]: value },
    }));
  }

  function getCellValue(rowIdx: number, field: string, original: string): string {
    return editedCells[rowIdx]?.[field] ?? original;
  }

  function isCellEdited(rowIdx: number, field: string): boolean {
    return editedCells[rowIdx]?.[field] != null;
  }

  async function saveRow(rowIdx: number) {
    const edits = editedCells[rowIdx];
    if (!edits || !dealId) return;

    const payload: Record<string, unknown> = {};
    for (const [field, value] of Object.entries(edits)) {
      if (field === "leased_sf" || field === "base_rent_annual_psf") {
        const num = parseFloat(value);
        if (isNaN(num)) { toast.error(`Invalid number for ${field}`); return; }
        if (field === "leased_sf" && num <= 0) { toast.error("SF must be > 0"); return; }
        payload[field] = num;
      } else if (field === "base_rent_annual") {
        const num = parseFloat(value.replace(/[,$]/g, ""));
        if (isNaN(num)) { toast.error("Invalid annual rent"); return; }
        payload[field] = num;
      } else if (field === "rent_escalation_value") {
        const num = parseFloat(value);
        if (isNaN(num)) { toast.error("Invalid escalation value"); return; }
        // Convert percent display back to decimal for percent-based types
        const escType = edits.rent_escalation_type || (dealId ? (deal?.property_data as any)?.tenants?.[rowIdx]?.rent_escalation_type : "flat");
        payload[field] = (escType === "fixed_percent" || escType === "CPI") ? num / 100 : num;
      } else if (field === "tenant_name" && !value.trim()) {
        toast.error("Tenant name cannot be empty"); return;
      } else {
        payload[field] = value;
      }
    }

    setSavingRow(rowIdx);
    try {
      await updateTenant(dealId, rowIdx, payload);
      setChangedRows(prev => new Set(prev).add(rowIdx));
      setEditedCells(prev => { const n = { ...prev }; delete n[rowIdx]; return n; });

      // Refresh deal data
      const freshDeal = await fetchDeal(dealId);
      setDeal(freshDeal);
      toast.success("Saved");
    } catch (e: any) {
      toast.error(friendlyError(e?.response?.data?.detail || "Couldn't save the change. Try again."));
    } finally {
      setSavingRow(null);
    }
  }

  async function handleDeleteTenant(idx: number) {
    if (!dealId) return;
    try {
      await deleteTenant(dealId, idx);
      setDeleteConfirm(null);
      setChangedRows(prev => new Set(prev).add(-1));
      const freshDeal = await fetchDeal(dealId);
      setDeal(freshDeal);
      toast.success("Tenant removed");
    } catch (e: any) {
      toast.error(friendlyError(e?.response?.data?.detail || "Couldn't delete that tenant. Try again."));
    }
  }

  async function handleAddTenant() {
    if (!dealId || !addForm.tenant_name || !addForm.square_footage || !addForm.base_rent_annual) {
      toast.error("Name, SF, and annual rent are required");
      return;
    }
    try {
      await addTenant(dealId, {
        tenant_name: addForm.tenant_name,
        suite_id: addForm.suite_id || undefined,
        square_footage: parseFloat(addForm.square_footage),
        base_rent_annual: parseFloat(addForm.base_rent_annual.replace(/[,$]/g, "")),
        lease_type: addForm.lease_type,
      });
      setShowAddModal(false);
      setAddForm({ tenant_name: "", suite_id: "", square_footage: "", base_rent_annual: "", lease_type: "pending" });
      setChangedRows(prev => new Set(prev).add(-1));
      const freshDeal = await fetchDeal(dealId);
      setDeal(freshDeal);
      toast.success("Tenant added");
    } catch (e: any) {
      toast.error(friendlyError(e?.response?.data?.detail || "Couldn't add the tenant. Try again."));
    }
  }

  function exitEditMode() {
    setEditMode(false);
    setEditedCells({});
    if (changedRows.size > 0) {
      // Refresh deal to pick up _needs_recalculation flag after edits
      if (dealId) {
        fetchDeal(dealId).then(setDeal).catch(() => {});
      }
      setChangedRows(new Set());
    }
  }

  // --- Detail view handlers ---

  async function openDetail(tenantId: string, inputType: string) {
    const idx = sorted.findIndex(t => t.tenant_id === tenantId);
    if (idx < 0) return;
    setDetailTenantIdx(idx);
    // Tab mapping: 0=Base Rent, 1=Free Rent, 2=%Rent, 3=Rollover, 4=CPI
    const tabMap: Record<string, number> = {
      rental_income: 0,
      base_rent: 0,
      free_rent: 1,
      pct_rent: 2,
      rollover: 3,
      cpi: 4,
    };
    setDetailTab(tabMap[inputType] ?? 0);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const data = await fetchTenantDetail(dealId!, tenantId, "rental_income");
      setDetailData(data);
      setDetailEntries((data.entries as TimeSeriesEntry[]) || []);
      setDetailDirty(false);
    } catch {
      setDetailData(null);
      setDetailEntries([]);
    } finally {
      setDetailLoading(false);
    }
  }

  function navigateDetail(direction: 1 | -1) {
    const newIdx = detailTenantIdx + direction;
    if (newIdx < 0 || newIdx >= sorted.length) return;
    const t = sorted[newIdx];
    openDetail(t.tenant_id, "rental_income");
  }

  async function saveDetail() {
    if (!dealId || !detailData) return;
    const tid = (detailData as Record<string, unknown>).tenant_id as string;
    setDetailSaving(true);
    try {
      const result = await saveTenantDetail(dealId, tid, "rental_income", detailEntries as unknown as Record<string, unknown>[]);
      setDetailData(result);
      setDetailEntries((result.entries as TimeSeriesEntry[]) || []);
      setDetailDirty(false);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setDetailSaving(false);
    }
  }

  async function resetDetailData() {
    if (!dealId || !detailData) return;
    const tid = (detailData as Record<string, unknown>).tenant_id as string;
    try {
      const result = await resetTenantDetail(dealId, tid, "rental_income");
      setDetailData(result);
      setDetailEntries((result.entries as TimeSeriesEntry[]) || []);
      setDetailDirty(false);
      toast.success("Reset to parsed values");
    } catch {
      toast.error("Reset failed");
    }
  }

  function handleEntryChange(entryId: string, key: string, value: unknown) {
    setDetailEntries(prev => prev.map(e =>
      e.entry_id === entryId ? { ...e, [key]: value, source: "manual" as const } : e
    ));
    setDetailDirty(true);
  }

  function handleEntryAdd(template: Record<string, unknown>) {
    const newEntry: TimeSeriesEntry = {
      entry_id: crypto.randomUUID(),
      start_date: new Date().toISOString().slice(0, 10),
      duration_years: 0,
      duration_months: 0,
      duration_days: 0,
      entry_type: (template.entry_type as string) || "base_rent",
      units: (template.units as string) || "$/SF/Year",
      amount: null,
      source: "manual",
      extra: {},
    };
    setDetailEntries(prev => [...prev, newEntry]);
    setDetailDirty(true);
  }

  function handleEntryDelete(entryId: string) {
    setDetailEntries(prev => prev.filter(e => e.entry_id !== entryId));
    setDetailDirty(true);
  }

  function exportCSV() {
    const headers = [
      "Suite", "Tenant", "SF", "% of NRA", "Lease Type",
      "Base Rent PSF", "Annual Rent", "Escalations", "Renewal Options",
      "Start Date", "Expiration Date",
    ];
    const rows = sorted.map((t) => [
      fmtSuite(t.suite_id),
      t.tenant_name,
      t.leased_sf,
      tla > 0 ? (() => { const pct = (t.leased_sf / tla) * 100; return pct >= 1 ? `${pct.toFixed(1)}%` : pct >= 0.1 ? `${pct.toFixed(2)}%` : pct > 0 ? "< 0.1%" : "0.0%"; })() : "—",
      formatLeaseTypeLabel(effectiveLeaseType(t)),
      (t.base_rent_annual_psf ?? 0).toFixed(2),
      ((t.base_rent_annual_psf ?? 0) * t.leased_sf).toFixed(2),
      escalationToCSV(t.rent_increase_schedule),
      formatRenewalInfo(t.renewal_info),
      t.lease_start_date,
      t.lease_expiration_date,
    ]);
    rows.push([
      "", "Total / Avg", totalOccupied,
      tla > 0 ? ((totalOccupied / tla) * 100).toFixed(1) + "%" : "—",
      "", avgRentPSF.toFixed(2), totalAnnualRent.toFixed(2),
      "", "", "", "",
    ]);
    downloadCSV([headers, ...rows], `${deal.name || "rent-roll"}_rent_roll.csv`);
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Print header */}
      <div className="print-header hidden print:block">
        <h1>{deal.name || "Rent Roll"}</h1>
        <p>{deal.address || ""} &middot; Rent Roll</p>
      </div>

      {/* Recalculate banner is handled by DealLayout (global banner
          that shows "Data has changed since the last projection" when
          _needs_recalculation flag is set on property_data) */}

      {/* Page header */}
      <div>
        <h2 className="font-display text-xl font-semibold text-bark-800">Rent Roll</h2>
        <p className="text-sm text-bark-400 mt-1">Tenant-by-tenant lease detail sorted by suite number</p>
      </div>

      {/* Summary bar */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-grove-300 px-6 py-4 shadow-sm">
        <div className="flex items-center gap-8 text-sm text-bark-500">
          <span>
            <span className="font-semibold text-bark-700">{sorted.length}</span>{" "}
            Tenants
          </span>
          <span>
            <span className="font-semibold text-bark-700">{fmtSF(totalOccupied)}</span>{" "}
            Occupied
          </span>
          {tla > 0 && (
            <span>
              <span className="font-semibold text-forest-700">{fmtPercent(totalOccupied / tla)}</span>{" "}
              Occupancy
              {tla > totalOccupied && (
                <span className="text-honey-700 ml-1">({fmtSF(tla - totalOccupied)} vacant)</span>
              )}
            </span>
          )}
          {tla > 0 && (
            <span>
              <span className="font-semibold text-bark-700">{fmtSF(tla)}</span>{" "}
              Total Leasable
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 print:hidden">
          {(hasPropertyData || parsedTenants.length > 0) && (
            <button
              onClick={() => editMode ? exitEditMode() : setEditMode(true)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                editMode
                  ? "bg-forest-700 text-white hover:bg-forest-600"
                  : "text-bark-500 bg-white border border-grove-300 hover:bg-grove-50 hover:shadow-sm"
              }`}
            >
              {editMode ? <Check className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
              {editMode ? "Done Editing" : "Edit Rent Roll"}
            </button>
          )}
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={async () => {
              if (!dealId) return;
              setDownloadingAllAbstracts(true);
              try {
                const blob = await downloadAllAbstracts(dealId);
                const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `${deal.name || "Property"} - Lease Abstracts.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                toast.error("Failed to download abstracts");
              } finally {
                setDownloadingAllAbstracts(false);
              }
            }}
            disabled={downloadingAllAbstracts}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all disabled:opacity-50"
          >
            <FileText className="w-4 h-4" />
            {downloadingAllAbstracts ? "Downloading..." : "All Abstracts"}
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        </div>
      </div>

      {/* Validation banner */}
      {validation && (validation.summary.errors > 0 || validation.summary.warnings > 0) && (
        <div className={`rounded-xl px-5 py-3 border print:hidden ${
          validation.summary.errors > 0
            ? "bg-red-50 border-red-200"
            : "bg-honey-50 border-honey-200"
        }`}>
          <div className="flex items-center gap-3">
            {validation.summary.errors > 0 ? (
              <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-honey-600 flex-shrink-0" />
            )}
            <div className="flex-1">
              <span className={`text-sm font-semibold ${validation.summary.errors > 0 ? "text-red-800" : "text-honey-800"}`}>
                {validation.summary.errors > 0
                  ? `${validation.summary.errors} error${validation.summary.errors !== 1 ? "s" : ""} found`
                  : ""}
                {validation.summary.errors > 0 && validation.summary.warnings > 0 ? ", " : ""}
                {validation.summary.warnings > 0
                  ? `${validation.summary.warnings} warning${validation.summary.warnings !== 1 ? "s" : ""}`
                  : ""}
                {" — review flagged values below"}
              </span>
              <span className="text-xs text-bark-400 ml-3">
                {validation.summary.checks_passed}/{validation.summary.checks_total} checks passed
                {validation.summary.tenants_flagged > 0 &&
                  ` · ${validation.summary.tenants_flagged} tenant${validation.summary.tenants_flagged !== 1 ? "s" : ""} flagged`}
              </span>
            </div>
          </div>
          {/* Document-level flags (tenant_id is null) */}
          {validation.flags.filter(f => !f.tenant_id).length > 0 && (
            <div className="mt-2 space-y-1 pl-8">
              {validation.flags.filter(f => !f.tenant_id).map((f, i) => (
                <div key={i} className={`text-xs px-2 py-1 rounded ${
                  f.severity === "error" ? "text-red-700 bg-red-100" : "text-honey-700 bg-honey-100"
                }`}>
                  {f.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {validation && validation.summary.errors === 0 && validation.summary.warnings === 0 && (
        <div className="rounded-xl px-5 py-3 border bg-forest-50 border-forest-200 print:hidden">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-forest-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-forest-800">
              All {validation.summary.checks_total} validation checks passed
            </span>
          </div>
        </div>
      )}

      {/* Occupied Tenants */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">
          Occupied Tenants ({sorted.length})
        </h3>
        <div className="bg-white rounded-xl border border-grove-300 overflow-x-auto shadow-sm">
          <table className="w-full min-w-[1200px] data-table text-[13px]">
            <thead>
              <tr>
                <th className="sticky top-0 left-0 z-20 bg-grove-100 min-w-[80px] w-[80px]">Suite</th>
                <th className="sticky top-0 left-[80px] z-20 bg-grove-100">Tenant</th>
                <th className="sticky top-0">Status</th>
                <th className="num sticky top-0 min-w-[80px]">SF</th>
                {tla > 0 && <th className="num sticky top-0">% of NRA</th>}
                <th className="sticky top-0">Lease Type</th>
                <th className="num sticky top-0">Base Rent PSF</th>
                <th className="num sticky top-0" title="Annual rent. Hover individual cells to see escalated rent as of analysis start.">Ann. Rent</th>
                <th className="sticky top-0 min-w-[110px]">Esc.</th>
                <th className="sticky top-0 min-w-[100px]">Renewal</th>
                <th className="sticky top-0 min-w-[85px]">Start</th>
                <th className="sticky top-0 min-w-[85px]">Expiration</th>
                <th className="sticky top-0 text-center min-w-[55px]" title="Exclude from general vacancy deduction">Vac. Exempt</th>
                {editMode && <th className="sticky top-0 w-8"></th>}
              </tr>
            </thead>
            <tbody>
              {sorted.map((t, displayIdx) => {
                const elt = effectiveLeaseType(t);
                // Find the original index in the unfiltered tenants array
                const origIdx = hasPropertyData
                  ? (prop!.tenants as unknown as ParsedTenant[]).indexOf(t)
                  : displayIdx;
                const isEdited = changedRows.has(origIdx);

                return (
                  <tr key={t.tenant_id} data-tenant={t.tenant_name} className={isEdited && !editMode ? "bg-honey-50/30" : ""}>
                    <td className="text-bark-400 whitespace-nowrap sticky left-0 z-10 sticky-col min-w-[80px] w-[80px]" data-field-id="suite_id" data-tenant={t.tenant_name}>
                      {editMode ? (
                        <EditableCell rowIdx={origIdx} field="suite_id" value={t.suite_id || ""} onChange={handleCellChange} getCellValue={getCellValue} isCellEdited={isCellEdited} onSave={saveRow} />
                      ) : fmtSuite(t.suite_id)}
                    </td>
                    <td className={`font-medium text-bark-700 max-w-[280px] sticky left-[80px] z-10 sticky-col ${getFlagClass(t.tenant_id, "tenant_name")}`} title={getFlagTooltip(t.tenant_id, "tenant_name")} data-field-id="tenant_name" data-tenant={t.tenant_name}>
                      {editMode ? (
                        <EditableCell rowIdx={origIdx} field="tenant_name" value={t.tenant_name} onChange={handleCellChange} getCellValue={getCellValue} isCellEdited={isCellEdited} onSave={saveRow} />
                      ) : (
                        <span className="flex items-center gap-1">
                          <span className="block truncate" title={t.tenant_name}>{t.tenant_name}</span>
                          <button
                            onClick={() => setAbstractTenant({ tenant: t, idx: origIdx })}
                            className="flex-shrink-0 text-bark-300 hover:text-forest-600 transition-colors print:hidden"
                            title="View lease abstract"
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      )}
                    </td>
                    <td><SpaceStatusBadge status={getSpaceStatus(t)} /></td>
                    <td className={`num ${getFlagClass(t.tenant_id, "leased_sf")}`} title={getFlagTooltip(t.tenant_id, "leased_sf")} data-field-id="leased_sf" data-tenant={t.tenant_name}>
                      {editMode ? (
                        <EditableCell rowIdx={origIdx} field="leased_sf" value={String(t.leased_sf)} onChange={handleCellChange} getCellValue={getCellValue} isCellEdited={isCellEdited} onSave={saveRow} numeric />
                      ) : fmtSF(t.leased_sf)}
                    </td>
                    {tla > 0 && <td className="num text-bark-400">{fmtPercent(t.leased_sf / tla, 1)}</td>}
                    <td>
                      <div className="flex items-center gap-1.5">
                        <LeaseTypeSelector type={elt} tenantId={t.tenant_id} dealId={dealId!} onTypeChange={handleLeaseTypeChange} />
                        {hasPercentageRent(t) && (
                          <button
                            type="button"
                            onClick={() => openDetail(t.tenant_id, "pct_rent")}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-honey-100 text-honey-800 border border-honey-200 hover:bg-honey-200 transition-colors"
                            title="Percentage rent configured"
                          >
                            %R
                          </button>
                        )}
                      </div>
                    </td>
                    <td className={`num text-bark-700 relative pr-6 ${getFlagClass(t.tenant_id, "base_rent_annual_psf")}`} title={getFlagTooltip(t.tenant_id, "base_rent_annual_psf")} data-field-id="base_rent_annual_psf" data-tenant={t.tenant_name}>
                      {editMode ? (
                        <EditableCell rowIdx={origIdx} field="base_rent_annual_psf" value={String(t.base_rent_annual_psf)} onChange={handleCellChange} getCellValue={getCellValue} isCellEdited={isCellEdited} onSave={saveRow} numeric />
                      ) : (
                        <>
                          {fmtPSF(t.base_rent_annual_psf)}
                          <span className="absolute right-1 top-1/2 -translate-y-1/2">
                            <DetailViewTrigger entityId={t.tenant_id} inputType="rental_income" onClick={openDetail} title="View rent detail" />
                          </span>
                        </>
                      )}
                    </td>
                    <td className="num text-bark-400 relative">{(() => {
                      if (editMode) {
                        const editedPsf = editedCells[origIdx]?.base_rent_annual_psf;
                        const editedSf = editedCells[origIdx]?.leased_sf;
                        const psf = editedPsf != null ? parseFloat(editedPsf) : t.base_rent_annual_psf;
                        const sf = editedSf != null ? parseFloat(editedSf) : t.leased_sf;
                        return fmtCurrency((!isNaN(psf) ? psf : 0) * (!isNaN(sf) ? sf : 0));
                      }
                      const escalatedPsf = getEscalatedPSF(t);
                      const baseAnn = t.base_rent_annual_psf * t.leased_sf;
                      const escalatedAnn = escalatedPsf * t.leased_sf;
                      const hasEscalation = Math.abs(escalatedPsf - t.base_rent_annual_psf) > 0.01;
                      return (
                        <span title={hasEscalation ? `Escalated to ${fmtPSF(escalatedPsf)} (${fmtCurrency(escalatedAnn)}/yr) as of ${analysisYear}` : ""}>
                          {fmtCurrency(baseAnn)}
                          {hasEscalation && <span className="absolute -left-2 top-1/2 -translate-y-1/2 text-[9px] text-forest-600">*</span>}
                        </span>
                      );
                    })()}</td>
                    <td className={`align-top ${getFlagClass(t.tenant_id, "rent_escalation_value")}`} title={getFlagTooltip(t.tenant_id, "rent_escalation_value")}>
                      {editMode ? (
                        <div className="flex items-center gap-1">
                          <select
                            value={getCellValue(origIdx, "rent_escalation_type", t.rent_escalation_type || "flat")}
                            onChange={(e) => { handleCellChange(origIdx, "rent_escalation_type", e.target.value); saveRow(origIdx); }}
                            className="text-[11px] border border-grove-200 rounded px-1 py-0.5 bg-transparent focus:ring-1 focus:ring-honey-400"
                          >
                            <option value="flat">Flat</option>
                            <option value="fixed_percent">% Annual</option>
                            <option value="fixed_amount">$ Annual</option>
                            <option value="CPI">CPI</option>
                            <option value="stepped">Stepped</option>
                          </select>
                          {!["flat", "stepped"].includes(getCellValue(origIdx, "rent_escalation_type", t.rent_escalation_type || "flat")) && (
                            <EditableCell rowIdx={origIdx} field="rent_escalation_value"
                              value={String(t.rent_escalation_type === "fixed_percent" || t.rent_escalation_type === "CPI" ? (t.rent_escalation_value || 0) * 100 : (t.rent_escalation_value || 0))}
                              onChange={handleCellChange} getCellValue={getCellValue} isCellEdited={isCellEdited} onSave={saveRow} numeric />
                          )}
                        </div>
                      ) : (
                        <div
                          className="min-w-[72px] cursor-pointer"
                          onClick={() => setRentStepTenantIdx(rentStepTenantIdx === origIdx ? null : origIdx)}
                          title="View/edit rent steps"
                        >
                          <EscalationCell schedule={t.rent_increase_schedule} escalationType={t.rent_escalation_type} escalationValue={t.rent_escalation_value} />
                        </div>
                      )}
                    </td>
                    <td className="text-bark-500 whitespace-nowrap">{formatRenewalInfo(t.renewal_info)}</td>
                    <td className={`whitespace-nowrap ${getFlagClass(t.tenant_id, "lease_dates")}`} title={getFlagTooltip(t.tenant_id, "lease_dates")} data-field-id="lease_start_date" data-tenant={t.tenant_name}>
                      {editMode ? (
                        <EditableCell rowIdx={origIdx} field="lease_start_date" value={t.lease_start_date || ""} onChange={handleCellChange} getCellValue={getCellValue} isCellEdited={isCellEdited} onSave={saveRow} type="date" />
                      ) : fmtDate(t.lease_start_date)}
                    </td>
                    <td className={`whitespace-nowrap ${getFlagClass(t.tenant_id, "lease_expiration_date") || getFlagClass(t.tenant_id, "lease_dates")}`} title={getFlagTooltip(t.tenant_id, "lease_expiration_date") || getFlagTooltip(t.tenant_id, "lease_dates")} data-field-id="lease_expiration_date" data-tenant={t.tenant_name}>
                      {editMode ? (
                        <EditableCell rowIdx={origIdx} field="lease_expiration_date" value={t.lease_expiration_date || ""} onChange={handleCellChange} getCellValue={getCellValue} isCellEdited={isCellEdited} onSave={saveRow} type="date" />
                      ) : (
                        <span className={`inline-flex items-center gap-1 ${isExpiringSoon(t.lease_expiration_date, prop?.analysis_start_date) ? "text-honey-700 font-medium" : ""}`}>
                          {fmtDate(t.lease_expiration_date)}
                          {isExpiringSoon(t.lease_expiration_date, prop?.analysis_start_date) && (
                            <span title="Expires within 24 months">
                              <AlertTriangle className="w-3.5 h-3.5 text-honey-600" />
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        checked={!!t.vacancy_exempt}
                        onChange={async (e) => {
                          try {
                            await updateTenant(dealId!, origIdx, { vacancy_exempt: e.target.checked });
                            const fresh = await fetchDeal(dealId!);
                            setDeal(fresh as Deal);
                          } catch {}
                        }}
                        className="w-3.5 h-3.5 accent-forest-600 cursor-pointer"
                        title={t.vacancy_exempt ? "Excluded from vacancy" : "Subject to vacancy"}
                      />
                    </td>
                    {editMode && (
                      <td className="text-center">
                        {deleteConfirm === origIdx ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleDeleteTenant(origIdx)} className="text-[10px] text-red-600 font-semibold hover:underline">Remove</button>
                            <button onClick={() => setDeleteConfirm(null)} className="text-[10px] text-bark-400 hover:underline">Cancel</button>
                          </div>
                        ) : (
                          <button onClick={() => setDeleteConfirm(origIdx)} className="text-bark-300 hover:text-red-500 transition-colors" title="Remove tenant">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {/* Add tenant row */}
              {editMode && (
                <tr>
                  <td colSpan={tla > 0 ? 14 : 13} className="text-center py-3">
                    <button onClick={() => setShowAddModal(true)} className="inline-flex items-center gap-2 text-sm text-forest-700 font-medium hover:text-forest-600 transition-colors">
                      <Plus className="w-4 h-4" /> Add Tenant
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td className="font-semibold text-bark-700">Total / Avg</td>
                <td></td>
                <td className="num">{fmtSF(totalOccupied)}</td>
                {tla > 0 && <td className="num">{fmtPercent(totalOccupied / tla)}</td>}
                <td></td>
                <td className="num">{fmtPSF(avgRentPSF)}</td>
                <td className="num text-forest-700 font-semibold">{fmtCurrency(totalAnnualRent)}</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td className="text-center text-[10px] text-bark-400">
                  {sorted.filter(t => t.vacancy_exempt).length > 0
                    ? `${sorted.filter(t => t.vacancy_exempt).length} exempt`
                    : ""}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        {sorted.some(t => Math.abs(getEscalatedPSF(t) - t.base_rent_annual_psf) > 0.01) && (
          <p className="text-[10px] text-bark-400 mt-1 px-1">
            <span className="text-forest-600">*</span> Rent has escalated since lease commencement. Hover for current rent as of {analysisYear}. The Pro Forma uses escalated rents.
          </p>
        )}
      </div>

      {/* Rent Step Editor */}
      {rentStepTenantIdx !== null && hasPropertyData && (() => {
        const tenants = prop!.tenants as unknown as ParsedTenant[];
        const t = tenants[rentStepTenantIdx];
        if (!t) return null;
        return (
          <RentStepEditor
            tenantIndex={rentStepTenantIdx}
            tenantName={t.tenant_name}
            leasedSf={t.leased_sf}
            leaseStart={t.lease_start_date || ""}
            leaseEnd={t.lease_expiration_date || ""}
            currentSteps={(t as any).rent_steps || []}
            escalationType={t.rent_escalation_type || "flat"}
            escalationValue={t.rent_escalation_value || 0}
            basePsf={t.base_rent_annual_psf}
            onSave={async (steps, escType) => {
              if (!dealId) return;
              await updateTenant(dealId, rentStepTenantIdx, {
                rent_steps: steps,
                rent_escalation_type: escType,
              });
        
              const freshDeal = await fetchDeal(dealId);
              setDeal(freshDeal);
              toast.success("Rent steps saved");
            }}
            onClose={() => setRentStepTenantIdx(null)}
          />
        );
      })()}

      {/* Available / Vacant Spaces */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3 flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5" />
          Available Spaces {vacantSpaces.length > 0 ? `(${vacantSpaces.length})` : ""}
        </h3>
        <div className="bg-white rounded-xl border border-grove-300 overflow-x-auto shadow-sm">
          <table className="w-full data-table text-[13px]">
            <thead>
              <tr>
                <th>Suite</th>
                <th>Space</th>
                <th className="num">SF</th>
                <th className="num">Asking Rent PSF</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {vacantSpaces.length > 0 ? (
                vacantSpaces.map((v, i) => (
                  <tr key={i} className="bg-honey-50/50">
                    <td className="text-bark-400">{v.unit}</td>
                    <td className="font-medium text-honey-800">{v.tenant_name}</td>
                    <td className="num">{v.gla_sf > 0 ? fmtSF(v.gla_sf) : "—"}</td>
                    <td className="num">{v.asking_rent_psf ? fmtPSF(v.asking_rent_psf) : ""}</td>
                    <td>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-honey-100 text-honey-800 border border-honey-200">
                        {v.status}
                      </span>
                    </td>
                    <td className="text-bark-400">
                      {v.begin_date && v.end_date
                        ? `${fmtDate(v.begin_date)} – ${fmtDate(v.end_date)}`
                        : "—"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center text-bark-400 py-6">
                    {(() => {
                      const totalSF = prop?.total_leasable_area || prop?.gross_building_area || parsedGLA || 0;
                      const occupiedSF = hasPropertyData
                        ? (prop!.tenants as unknown as ParsedTenant[]).reduce((s, t) => s + (t.leased_sf || 0), 0)
                        : sorted.reduce((s, t) => s + (t.leased_sf || 0), 0);
                      const impliedVacantSF = totalSF - occupiedSF;
                      if (impliedVacantSF > 100) {
                        const occPct = totalSF > 0 ? fmtPercent(occupiedSF / totalSF, 1) : "—";
                        return `No vacant suites identified in rent roll — ${fmtSF(impliedVacantSF)} implied vacancy (${occPct} occupied)`;
                      }
                      return "No available suites — property is fully leased";
                    })()}
                  </td>
                </tr>
              )}
            </tbody>
            {vacantSpaces.length > 0 && (
              <tfoot>
                <tr>
                  <td></td>
                  <td className="font-semibold text-bark-700">Total Vacant</td>
                  <td className="num font-semibold">{fmtSF(vacantSpaces.reduce((s, v) => s + v.gla_sf, 0))}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Miscellaneous Income */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3 flex items-center gap-2">
          <Banknote className="w-3.5 h-3.5" />
          Miscellaneous Income {miscIncome.length > 0 ? `(${miscIncome.length})` : ""}
        </h3>
        <div className="bg-white rounded-xl border border-grove-300 overflow-x-auto shadow-sm">
          <table className="w-full data-table text-[13px]">
            <thead>
              <tr>
                <th>Income Source</th>
                <th>Location</th>
                <th className="num">Annual Amount</th>
                <th>Term</th>
              </tr>
            </thead>
            <tbody>
              {miscIncome.length > 0 ? (
                miscIncome.map((item, i) => (
                  <tr key={i}>
                    <td className="font-medium text-bark-700">{item.source}</td>
                    <td className="text-bark-400">{item.location}</td>
                    <td className="num">{item.annual_amount > 0 ? fmtCurrency(item.annual_amount) : "—"}</td>
                    <td className="text-bark-400">
                      {item.begin_date && item.end_date
                        ? `${fmtDate(item.begin_date)} – ${fmtDate(item.end_date)}`
                        : "—"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center text-bark-400 py-6">
                    No miscellaneous income items found. Parking, storage, antenna, and other non-base-rent income will appear here when extracted from rent rolls or operating statements.
                  </td>
                </tr>
              )}
            </tbody>
            {miscIncome.length > 0 && (
              <tfoot>
                <tr>
                  <td className="font-semibold text-bark-700">Total Misc Income</td>
                  <td></td>
                  <td className="num font-semibold text-forest-700">
                    {fmtCurrency(miscIncome.reduce((s, item) => s + item.annual_amount, 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Add Tenant Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-xl border border-grove-300 shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold text-bark-800 mb-4">Add Tenant</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-bark-500 mb-1">Tenant Name *</label>
                <input value={addForm.tenant_name} onChange={e => setAddForm(p => ({ ...p, tenant_name: e.target.value }))} className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-bark-500 mb-1">Suite</label>
                  <input value={addForm.suite_id} onChange={e => setAddForm(p => ({ ...p, suite_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-bark-500 mb-1">Square Footage *</label>
                  <input value={addForm.square_footage} onChange={e => setAddForm(p => ({ ...p, square_footage: e.target.value }))} className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400 tabular-nums" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-bark-500 mb-1">Annual Rent *</label>
                  <input value={addForm.base_rent_annual} onChange={e => setAddForm(p => ({ ...p, base_rent_annual: e.target.value }))} placeholder="e.g. 120000" className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400 tabular-nums" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-bark-500 mb-1">Lease Type</label>
                  <select value={addForm.lease_type} onChange={e => setAddForm(p => ({ ...p, lease_type: e.target.value }))} className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400">
                    <option value="pending">Pending</option>
                    <option value="nnn">NNN</option>
                    <option value="gross">Gross</option>
                    <option value="modified_gross">Modified Gross</option>
                    <option value="base_year_stop">Base Year Stop</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm text-bark-500 hover:text-bark-700 transition-colors">Cancel</button>
              <button onClick={handleAddTenant} className="px-4 py-2 bg-forest-700 text-white text-sm font-semibold rounded-lg hover:bg-forest-600 transition-all">Add Tenant</button>
            </div>
          </div>
        </div>
      )}

      {/* Effective Gross Revenue Summary */}
      {(() => {
        const totalMisc = miscIncome.reduce((s, item) => s + item.annual_amount, 0);
        const effectiveGross = totalAnnualRent + totalMisc;
        return (
          <div className="bg-white rounded-xl border border-grove-300 shadow-sm px-6 py-4">
            <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">Revenue Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-bark-600">Occupied Base Rent</span>
                <span className="font-medium text-bark-700 tabular-nums">{fmtCurrency(totalAnnualRent)}</span>
              </div>
              {totalMisc > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-bark-600">Miscellaneous Income</span>
                  <span className="font-medium text-bark-700 tabular-nums">{fmtCurrency(totalMisc)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-grove-300">
                <span className="font-semibold text-bark-800">Total Base Rent</span>
                <span className="font-bold text-forest-700 tabular-nums text-base">{fmtCurrency(effectiveGross)}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Detail View Panel */}
      <RentRollDetailPanel
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        sorted={sorted}
        detailTenantIdx={detailTenantIdx}
        detailTab={detailTab}
        onTabChange={setDetailTab}
        detailData={detailData}
        detailEntries={detailEntries}
        detailDirty={detailDirty}
        detailSaving={detailSaving}
        detailLoading={detailLoading}
        onPrev={detailTenantIdx > 0 ? () => navigateDetail(-1) : null}
        onNext={detailTenantIdx < sorted.length - 1 ? () => navigateDetail(1) : null}
        onEntryChange={handleEntryChange}
        onEntryAdd={handleEntryAdd}
        onEntryDelete={handleEntryDelete}
        onSave={saveDetail}
        onReset={resetDetailData}
      />

      {/* Lease Abstract Modal */}
      {abstractTenant && (
        <LeaseAbstractModal
          tenant={abstractTenant.tenant}
          propertyName={prop?.name || deal.name || "Property"}
          dealId={dealId!}
          tenantIdx={abstractTenant.idx}
          onClose={() => setAbstractTenant(null)}
        />
      )}
    </div>
  );
}

function formatLeaseTypeLabel(type: string): string {
  const map: Record<string, string> = {
    NNN: "NNN", NN: "NN", gross: "Gross", modified_gross: "Modified Gross",
    base_year_stop: "Base Year Stop", FSG: "Full Service Gross", pending: "Pending",
  };
  return map[type.toLowerCase()] || type;
}

function EscalationCell({ schedule, escalationType, escalationValue }: {
  schedule: EscalationStep[];
  escalationType?: string;
  escalationValue?: number;
}) {
  if (!schedule || schedule.length === 0) {
    // Fall back to rent_escalation_type / rent_escalation_value
    if (escalationType === "fixed_percent" && escalationValue) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-forest-50 text-forest-700 border border-forest-200">
          {fmtPercent(escalationValue, 1)}/yr
        </span>
      );
    }
    if (escalationType === "CPI") {
      const spread = escalationValue ? ` + ${fmtPercent(escalationValue, 1)}` : "";
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-sage-100 text-sage-600 border border-sage-200">
          CPI{spread}
        </span>
      );
    }
    if (escalationType === "fixed_amount" && escalationValue) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-forest-50 text-forest-700 border border-forest-200">
          {fmtPSF(escalationValue)}/yr
        </span>
      );
    }
    if (escalationType === "flat") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-grove-50 text-bark-400 border border-grove-200">
          Flat
        </span>
      );
    }
    return <span className="text-bark-400 text-[11px]">{"\u2014"}</span>;
  }

  const first = schedule[0];

  // Percent annual — simple display
  if (first.type === "percent_annual") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-forest-50 text-forest-700 border border-forest-200">
        {fmtPercentRaw(first.percent, 1)}/yr
      </span>
    );
  }

  if (first.type === "stepped") {
    const steps = schedule.filter((s) => s.type === "stepped");
    const shown = steps.slice(0, 2);
    const remaining = steps.length - shown.length;
    const fullTitle = steps.map(s => `${s.year_range}: ${fmtCurrency(s.annual_amount || 0)}`).join(", ");
    return (
      <div className="space-y-0.5" title={fullTitle}>
        {shown.map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-[11px]">
            <span className="text-bark-400 w-[52px] flex-shrink-0">{s.year_range}</span>
            <span className="text-bark-700 font-medium tabular-nums whitespace-nowrap">{fmtCurrency(s.annual_amount || 0)}</span>
          </div>
        ))}
        {remaining > 0 && (
          <span className="text-bark-400 text-[10px]">+{remaining} more</span>
        )}
      </div>
    );
  }

  // Text fallback
  if (first.type === "text") {
    return <span className="text-bark-500 text-[11px]">{first.description}</span>;
  }

  return <span className="text-bark-400">—</span>;
}

function formatRenewalInfo(info: unknown): string {
  if (!info) return "—";
  if (typeof info === "string") return info;
  if (typeof info === "object") {
    const obj = info as Record<string, unknown>;
    const parts: string[] = [];
    if (obj.num_opts) parts.push(`${obj.num_opts} option(s)`);
    if (obj.years) parts.push(`${obj.years} yr`);
    if (obj.option_psf) {
      const psf = Number(obj.option_psf);
      parts.push(isNaN(psf) ? String(obj.option_psf) : fmtPSF(psf));
    }
    if (obj.notice_date) parts.push(`notice: ${obj.notice_date}`);
    return parts.length > 0 ? parts.join(", ") : "—";
  }
  return String(info);
}

function fmtSuite(suite: string | null): string {
  if (!suite) return "—";
  return suite.replace(/\band\b/gi, "&");
}

function parseSuiteNum(suite: string | null): number {
  if (!suite) return Infinity;
  const match = suite.match(/^\d+/);
  return match ? parseInt(match[0], 10) : Infinity;
}

function escalationToCSV(schedule: EscalationStep[]): string {
  if (!schedule || schedule.length === 0) return "";
  const first = schedule[0];
  if (first.type === "percent_annual") return `${first.percent}%/yr`;
  if (first.type === "stepped") {
    return schedule
      .filter((s) => s.type === "stepped")
      .map((s) => `${s.year_range}: $${(s.annual_amount || 0).toLocaleString()}`)
      .join("; ");
  }
  if (first.type === "text") return first.description || "";
  return "";
}

function isExpiringSoon(dateStr: string, analysisStartDate?: string): boolean {
  if (!dateStr) return false;
  const exp = new Date(dateStr);
  const reference = analysisStartDate ? new Date(analysisStartDate) : new Date();
  const diffMs = exp.getTime() - reference.getTime();
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.44);
  return diffMonths <= 24 && diffMonths > 0;
}

/** Check if a tenant has percentage rent configured (from property_data). */
function hasPercentageRent(t: ParsedTenant | Tenant): boolean {
  const raw = t as unknown as Record<string, unknown>;
  const pctType = raw.percentage_rent_type as string | undefined;
  if (pctType && pctType !== "none") return true;
  const pct = raw.percentage_rent_pct as number | undefined;
  return typeof pct === "number" && pct > 0;
}

function getSpaceStatus(t: ParsedTenant | Tenant): SpaceStatus | "expired" {
  // Check if lease has expired before returning default status
  const expDate = (t as ParsedTenant).lease_expiration_date || (t as unknown as Record<string, unknown>).lease_expiration_date as string;
  if (expDate) {
    const exp = new Date(expDate);
    if (!isNaN(exp.getTime()) && exp < new Date()) {
      return "expired";
    }
  }
  const raw = (t as unknown as Record<string, unknown>).space_status as SpaceStatus | undefined;
  return raw || "leased";
}

function EditableCell({
  rowIdx, field, value, onChange, getCellValue, isCellEdited, onSave, numeric, type,
}: {
  rowIdx: number; field: string; value: string;
  onChange: (r: number, f: string, v: string) => void;
  getCellValue: (r: number, f: string, orig: string) => string;
  isCellEdited: (r: number, f: string) => boolean;
  onSave: (r: number) => void;
  numeric?: boolean; type?: string;
}) {
  const cellVal = getCellValue(rowIdx, field, value);
  const edited = isCellEdited(rowIdx, field);
  return (
    <input
      type={type || "text"}
      value={cellVal}
      onChange={e => onChange(rowIdx, field, e.target.value)}
      onBlur={() => { if (edited) onSave(rowIdx); }}
      onKeyDown={e => { if (e.key === "Enter" && edited) onSave(rowIdx); if (e.key === "Escape") onChange(rowIdx, field, value); }}
      className={`w-full px-1.5 py-0.5 text-[13px] border rounded focus:outline-none focus:ring-1 focus:ring-honey-400 ${
        edited ? "bg-honey-50 border-honey-300" : "border-grove-200 bg-transparent"
      } ${numeric ? "tabular-nums text-right" : ""}`}
      style={{ minWidth: numeric ? 70 : 80 }}
    />
  );
}

function SpaceStatusBadge({ status }: { status: SpaceStatus | "expired" }) {
  if (status === "expired") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800 border border-red-200">
        Expired
      </span>
    );
  }
  if (status === "speculative") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-honey-100 text-honey-800 border border-honey-200">
        Speculative
      </span>
    );
  }
  if (status === "dark_tenant") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-800 border border-purple-200">
        Dark Tenant
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-forest-50 text-forest-700 border border-forest-200">
      Leased
    </span>
  );
}
